import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
  deleteDoc
} from 'firebase/firestore';
// Étiquette "Complet" + liste d'attente : voir CourseForm.js / CourseEdit.js
// (champ isFull) et la collection waitlist (écrite par Registration.js, le
// formulaire d'inscription public).
import {
  Button,
  Box,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  fetchDanceStyles,
  getDanceStyleLabel,
} from '../services/danceStylesService';

function Courses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [danceStyleOptions, setDanceStyleOptions] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [danceCourses, setDanceCourses] = useState([]);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [waitlistCounts, setWaitlistCounts] = useState({}); // { [courseId]: nombre en attente }

  useEffect(() => {
    const fetchData = async () => {
      const styles = await fetchDanceStyles();
      setDanceStyleOptions(styles);

      // Charger les cours depuis dance_courses
      const coursesSnapshot = await getDocs(collection(db, 'dance_courses'));
      setCourses(coursesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Compter la liste d'attente par cours (collection alimentée par le
      // formulaire d'inscription public quand un cours est marqué complet)
      try {
        const waitlistSnapshot = await getDocs(collection(db, 'waitlist'));
        const counts = {};
        waitlistSnapshot.forEach(docSnap => {
          const cid = docSnap.data().courseId;
          if (cid) counts[cid] = (counts[cid] || 0) + 1;
        });
        setWaitlistCounts(counts);
      } catch (error) {
        console.error('Erreur chargement liste d\'attente:', error);
      }

      // Charger les professeurs
      const teachersQuery = query(collection(db, 'users'), where('role', '==', 'teacher'));
      const teachersSnapshot = await getDocs(teachersQuery);
      setTeachers(teachersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Charger les élèves
      const studentsQuery = query(collection(db, 'users'), where('role', '==', 'student'));
      const studentsSnapshot = await getDocs(studentsQuery);
      setStudents(studentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Charger les parents
      const parentsQuery = query(collection(db, 'users'), where('role', '==', 'parent'));
      const parentsSnapshot = await getDocs(parentsQuery);
      setParents(parentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Charger les cours de danse (associations si nécessaires)
      const danceCoursesSnapshot = await getDocs(collection(db, 'dance_courses'));
      setDanceCourses(danceCoursesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    fetchData();
  }, []);

  const handleEnroll = async () => {
    try {
      await updateDoc(doc(db, 'dance_courses', selectedCourse.id), {
        enrolledStudents: selectedCourse.enrolledStudents,
        enrolledParents: selectedCourse.enrolledParents
      });
      setEnrollOpen(false);
      const coursesSnapshot = await getDocs(collection(db, 'dance_courses'));
      setCourses(coursesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error("Erreur inscription: ", error);
    }
  };

  const handleDeleteCourse = async () => {
    if (!courseToDelete) return;
    
    try {
      await deleteDoc(doc(db, 'dance_courses', courseToDelete.id));
      setCourses(prev => prev.filter(c => c.id !== courseToDelete.id));
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error("Erreur suppression :", error);
    }
  };

  const columns = [
    {
      field: 'title',
      headerName: 'Titre',
      width: 220,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {params.row.title}
          {params.row.isNewCourse && (
            <Chip label="🆕 NEW" size="small" color="warning" sx={{ fontWeight: 'bold' }} />
          )}
          {params.row.isFull && (
            <Chip label="🔒 Complet" size="small" color="error" sx={{ fontWeight: 'bold' }} />
          )}
        </Box>
      )
    },
    {
      field: 'difficultyLevels',
      headerName: 'Niveau',
      width: 150,
      renderCell: (params) => {
        const levels = params.row.difficultyLevels || [];
        return levels.map((level, i) => (
          <Chip key={i} label={level} size="small" sx={{ mr: 0.5 }} />
        ));
      }
    },
    {
      field: 'danceStyles',
      headerName: 'Styles de danse',
      width: 260,
      renderCell: (params) => {
        const styles = Array.isArray(params.row.danceStyles) ? params.row.danceStyles : [];
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
            {styles.length > 0
              ? styles.map(styleId => <Chip key={styleId} label={getDanceStyleLabel(danceStyleOptions, styleId)} size="small" />)
              : <Typography variant="caption" color="text.secondary">Non renseigné</Typography>}
          </Box>
        );
      }
    },
    { field: 'description', headerName: 'Description', width: 300 },
    {
      field: 'teachers',
      headerName: 'Professeurs',
      width: 250,
      renderCell: (params) => {
        const teachers = params.row.teachers || [];
        return (
          <div>
            {teachers.map((t, index) => (
              <Chip
                key={index}
                label={t.pseudo || '—'}
                size="small"
                style={{ marginRight: 4, marginBottom: 4 }}
              />
            ))}
          </div>
        );
      }
    },
    {
      field: 'daysOfWeek',
      headerName: 'Jours',
      width: 150,
      renderCell: (params) => {
        // Extraire les jours uniques
        const uniqueDays = [...new Set(
          (params.row.dates || []).map(d => d.day)
        )];
        
        return uniqueDays.map((day, i) => (
          <Chip key={i} label={day} size="small" sx={{ mr: 0.5 }} />
        ));
      }
    },
    {
      field: 'dates',
      headerName: 'Dates',
      width: 300,
      renderCell: (params) => {
        return (
          <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
            {(params.row.dates || []).map((dateObj, index) => {
              const date = new Date(dateObj.date);
              const endDate = new Date(date.getTime() + dateObj.duration * 60000);
              
              const formattedDate = date.toLocaleString('fr-FR', {
                weekday: 'short',
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
              });
              
              const formattedEnd = endDate.toLocaleString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit'
              });
              
              return <div key={index}>{formattedDate} - {formattedEnd}</div>;
            })}
          </div>
        );
      }
    },
    {
      field: 'enrolled',
      headerName: 'Inscrits',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={`${params.row.enrolledStudents?.length || 0} élèves / ${params.row.enrolledParents?.length || 0} parents`}
        />
      )
    },
    {
      field: 'waitlist',
      headerName: "Liste d'attente",
      width: 140,
      renderCell: (params) => {
        const count = waitlistCounts[params.row.id] || 0;
        return count > 0
          ? <Chip label={`${count} en attente`} size="small" color="warning" />
          : <Typography variant="caption" color="text.secondary">—</Typography>;
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedCourse(params.row);
              setEnrollOpen(true);
            }}
          >
            Inscrire
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            component={Link}
            to={`/courses/edit/${params.row.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            Modifier
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setCourseToDelete(params.row);
              setDeleteDialogOpen(true);
            }}
          >
            Supprimer
          </Button>
        </Box>
      )
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Gestion des Cours</Typography>
        <Button
          variant="contained"
          component={Link}
          to="/courses/new"
        >
          Nouveau Cours
        </Button>
      </Box>

      <div style={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={courses}
          columns={columns}
          pageSize={10}
          getRowId={(row) => row.id}
          getRowHeight={() => 'auto'}
          onRowClick={(params, event) => {
            // Ne pas naviguer si le clic vient d'un bouton dans les actions
            if (!event.target.closest('.MuiDataGrid-actionsCell')) {
              navigate(`/courses/details/${params.row.id}`);
            }
          }}
        />
      </div>

      {/* Dialog inscription */}
      <Dialog open={enrollOpen} onClose={() => setEnrollOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Inscrire des élèves et parents au cours</DialogTitle>
        <DialogContent>
          {selectedCourse && (
            <>
              <Typography variant="h6" sx={{ mt: 2 }}>Élèves</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {students.map(student => (
                  <Chip
                    key={student.id}
                    label={`${student.firstName} ${student.lastName}`}
                    color={selectedCourse.enrolledStudents?.includes(student.id) ? 'primary' : 'default'}
                    onClick={() => {
                      const updated = [...(selectedCourse.enrolledStudents || [])];
                      if (updated.includes(student.id)) {
                        setSelectedCourse({
                          ...selectedCourse,
                          enrolledStudents: updated.filter(id => id !== student.id)
                        });
                      } else if (updated.length < selectedCourse.maxStudents) {
                        setSelectedCourse({
                          ...selectedCourse,
                          enrolledStudents: [...updated, student.id]
                        });
                      }
                    }}
                  />
                ))}
              </Box>

              <Typography variant="h6">Parents</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {parents.map(parent => (
                  <Chip
                    key={parent.id}
                    label={`${parent.firstName} ${parent.lastName}`}
                    color={selectedCourse.enrolledParents?.includes(parent.id) ? 'primary' : 'default'}
                    onClick={() => {
                      const updated = [...(selectedCourse.enrolledParents || [])];
                      if (updated.includes(parent.id)) {
                        setSelectedCourse({
                          ...selectedCourse,
                          enrolledParents: updated.filter(id => id !== parent.id)
                        });
                      } else {
                        setSelectedCourse({
                          ...selectedCourse,
                          enrolledParents: [...updated, parent.id]
                        });
                      }
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollOpen(false)}>Annuler</Button>
          <Button onClick={handleEnroll} variant="contained">Enregistrer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmation suppression */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer le cours "{courseToDelete?.title}" ? 
            Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
          <Button 
            onClick={handleDeleteCourse} 
            variant="contained" 
            color="error"
            autoFocus
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Courses;